import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import api from "../../utils/api";
import { getInvoicesList, downloadInvoicePdf } from "../../services/invoiceService";

const PLAN_PRICES = {
  Basic: 100,
  Pro: 200,
  Premium: 300,
};

const CAMERA_PRICE = 100;
const DESCRIPTION_OPTIONS = [
  { value: "call_out", label: "Call Out" },
  { value: "repair", label: "Repair" },
  { value: "maintenance", label: "Maintenance" },
  { value: "replacement", label: "Replacement" },
  { value: "custom", label: "Custom description" },
];

const FILTER_DEFAULTS = Object.freeze({
  clientId: "",
  date: "",
  startDate: "",
  endDate: "",
});

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
};

export default function InvoicesPage() {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [plan, setPlan] = useState("");
  const [years, setYears] = useState("");
  const [cameras, setCameras] = useState("");
  const [descriptionType, setDescriptionType] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [sending, setSending] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [filters, setFilters] = useState(() => ({ ...FILTER_DEFAULTS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...FILTER_DEFAULTS }));
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await api.get("/non-staff-users/");
        const list = response?.data || response || [];
        setClients(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Failed to load clients", error);
        setSnackbar({ open: true, message: "Failed to load clients." });
      } finally {
        setLoadingClients(false);
      }
    };

    loadClients();
  }, []);

  const fetchInvoices = useCallback(
    async (filtersOverride) => {
      const activeFilters = filtersOverride ?? appliedFilters;
      setLoadingInvoices(true);
      try {
        const list = await getInvoicesList(activeFilters);
        setInvoices(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Failed to load invoices", error);
        setSnackbar({ open: true, message: "Failed to load invoices." });
      } finally {
        setLoadingInvoices(false);
      }
    },
    [appliedFilters]
  );

  useEffect(() => {
    fetchInvoices(appliedFilters);
  }, [appliedFilters, fetchInvoices]);

  const selectedClient = useMemo(
    () => clients.find((client) => String(client.id) === String(selectedClientId)) || null,
    [clients, selectedClientId]
  );

  const resolvedDescription = useCallback(() => {
    if (descriptionType === "custom") {
      return customDescription.trim();
    }
    const option = DESCRIPTION_OPTIONS.find((opt) => opt.value === descriptionType);
    return option ? option.label : "";
  }, [descriptionType, customDescription]);

  const total = useMemo(() => {
    const manual = Number(manualPrice);
    const manualValid = !Number.isNaN(manual) && manual > 0;
    if (manualValid) {
      return manual;
    }
    const planPrice = plan ? PLAN_PRICES[plan] ?? 0 : 0;
    const yearsCount = Number(years) || 0;
    const cameraCount = Number(cameras) || 0;
    return planPrice * yearsCount + CAMERA_PRICE * cameraCount;
  }, [manualPrice, plan, years, cameras]);
  const pdfPreviewText = useMemo(() => {
    const description = resolvedDescription() || "-";
    const safePlan = plan || "-";
    const safeYears = years || "0";
    const safeCameras = cameras || "0";
    return [
      "Itemized Charges",
      "============================================================",
      "Description                     Amount",
      "------------------------------------------------------------",
      `Description                    ${description}`,
      `Plan                           ${safePlan}`,
      `Years of Protection            ${safeYears}`,
      `Cameras                        ${safeCameras}`,
      `Invoice Total                  GBP ${Number(total || 0).toFixed(2)}`,
      "============================================================",
    ].join("\n");
  }, [resolvedDescription, plan, years, cameras, total]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      if (field === "date") {
        const shouldClearRange = Boolean(value);
        return {
          ...prev,
          date: value,
          startDate: shouldClearRange ? "" : prev.startDate,
          endDate: shouldClearRange ? "" : prev.endDate,
        };
      }

      if (field === "startDate" || field === "endDate") {
        const shouldClearDate = Boolean(value);
        return {
          ...prev,
          [field]: value,
          date: shouldClearDate ? "" : prev.date,
        };
      }

      return { ...prev, [field]: value };
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const handleResetFilters = () => {
    const reset = { ...FILTER_DEFAULTS };
    setFilters(reset);
    setAppliedFilters(reset);
  };

  const resetDialogFields = useCallback(() => {
    setSelectedClientId("");
    setPlan("");
    setYears("");
    setCameras("");
    setDescriptionType("");
    setCustomDescription("");
    setManualPrice("");
  }, []);

  const handleDialogClose = () => {
    if (!sending) {
      setDialogOpen(false);
      resetDialogFields();
    }
  };

  const handleDialogOpen = () => {
    resetDialogFields();
    setDialogOpen(true);
  };

  const sendInvoice = async () => {
    if (!selectedClient) {
      setSnackbar({ open: true, message: "Please select a client." });
      return;
    }

    const description = resolvedDescription();
    if (descriptionType === "custom" && !description) {
      setSnackbar({ open: true, message: "Please enter a custom description." });
      return;
    }

    const manualAmount = Number(manualPrice);
    const planPrice = plan ? PLAN_PRICES[plan] ?? 0 : 0;
    const yearsValue = Number(years) || 0;
    const camerasValue = Number(cameras) || 0;
    const computedTotal = manualAmount > 0 ? manualAmount : planPrice * yearsValue + CAMERA_PRICE * camerasValue;

    if (computedTotal <= 0 || Number.isNaN(computedTotal)) {
      setSnackbar({ open: true, message: "Please provide pricing details for this invoice." });
      return;
    }

    try {
      setSending(true);
      const filename = `Invoice_${selectedClient.id}_${Date.now()}.pdf`;
      await api.post("/send-invoice-email/", {
        client: selectedClient.id,
        plan,
        years: yearsValue,
        cameras: camerasValue,
        total: computedTotal,
        description,
        filename,
      });

      setSnackbar({ open: true, message: "Invoice emailed successfully." });
      setDialogOpen(false);
      resetDialogFields();
      await fetchInvoices(appliedFilters);
    } catch (error) {
      console.error("Failed to send invoice", error);
      const message = error?.response?.data?.error || "Failed to email invoice.";
      setSnackbar({ open: true, message });
    } finally {
      setSending(false);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      await downloadInvoicePdf(invoice.invoice_id, `Invoice_${invoice.invoice_id}.pdf`);
    } catch (error) {
      console.error("Failed to download invoice", error);
      setSnackbar({ open: true, message: "Unable to download invoice. Please try again." });
    }
  };

  return (
    <Container sx={{ mt: 5 }}>
      <Typography variant="h4" gutterBottom>
        Invoices
      </Typography>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" onClick={handleDialogOpen}>
          Create Invoice
        </Button>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Stored Invoices
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel id="filter-client-label">Client</InputLabel>
            <Select
              labelId="filter-client-label"
              label="Client"
              value={filters.clientId}
              onChange={(e) => handleFilterChange("clientId", String(e.target.value))}
            >
              <MenuItem value="">All Clients</MenuItem>
              {clients.map((client) => {
                const name = `${client.first_name || ""} ${client.last_name || ""}`.trim();
                return (
                  <MenuItem key={client.id} value={String(client.id)}>
                    {name || client.user?.username || client.id}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            label="Invoice Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.date}
            onChange={(e) => handleFilterChange("date", e.target.value)}
            helperText="Search for a specific day"
          />

          <TextField
            label="Start Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate}
            onChange={(e) => handleFilterChange("startDate", e.target.value)}
          />

          <TextField
            label="End Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate}
            onChange={(e) => handleFilterChange("endDate", e.target.value)}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button variant="outlined" onClick={handleResetFilters}>
            Reset
          </Button>
          <Button variant="contained" onClick={handleApplyFilters} disabled={loadingInvoices}>
            Apply Filters
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Stored Invoices</Typography>
          <Button size="small" onClick={() => fetchInvoices(appliedFilters)} disabled={loadingInvoices}>
            Refresh
          </Button>
        </Box>

        {loadingInvoices ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography>Loading invoices.</Typography>
          </Box>
        ) : invoices.length === 0 ? (
          <Typography color="text.secondary">No invoices match your filters.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={64}></TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Document</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => {
                const clientLabel = invoice.client_name || invoice.client_email || invoice.client;
                const isExpanded = expandedInvoiceId === invoice.invoice_id;
                const lineItems = Array.isArray(invoice?.details?.line_items) ? invoice.details.line_items : [];
                return (
                  <React.Fragment key={invoice.invoice_id}>
                    <TableRow
                      hover
                      onClick={() =>
                        setExpandedInvoiceId((prev) =>
                          prev === invoice.invoice_id ? null : invoice.invoice_id
                        )
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <IconButton size="small" aria-label="expand invoice">
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>#{invoice.invoice_id}</TableCell>
                      <TableCell>{clientLabel}</TableCell>
                      <TableCell>{formatCurrency(invoice.total_amount)}</TableCell>
                      <TableCell>{formatDate(invoice.invoice_datetime)}</TableCell>
                      <TableCell>{invoice.is_paid ? "Paid" : "Pending"}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDownloadInvoice(invoice);
                          }}
                        >
                          Download PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 2, py: 2, backgroundColor: "#fafafa" }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Invoice Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Billing month/year: {invoice.billing_month || "-"} / {invoice.billing_year || "-"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Sent: {invoice.invoice_sent ? "Yes" : "No"}
                            </Typography>

                            {lineItems.length > 0 ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Value</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {lineItems.map((item, index) => (
                                    <TableRow key={`${invoice.invoice_id}-item-${index}`}>
                                      <TableCell>{item?.label || "-"}</TableCell>
                                      <TableCell>{item?.value ?? "-"}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No detailed line items available for this invoice.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Accordion disableGutters sx={{ borderRadius: 2, boxShadow: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography variant="h6">
              Invoice Preview - {new Date().toLocaleDateString()}
            </Typography>
            <Typography variant="body2">Total: {formatCurrency(total)}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{
              margin: 0,
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: 14,
              backgroundColor: "#f8f9fa",
              p: 2,
              borderRadius: 1,
            }}
          >
            {pdfPreviewText}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Invoice</DialogTitle>
        <DialogContent dividers>
          {loadingClients ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography>Loading clients…</Typography>
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel id="client-select">Client</InputLabel>
                <Select
                  labelId="client-select"
                  label="Client"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(String(e.target.value))}
                >
                  {clients.map((client) => {
                    const name = `${client.first_name || ""} ${client.last_name || ""}`.trim();
                    return (
                      <MenuItem key={client.id} value={String(client.id)}>
                        {name || client.username || client.email || client.id}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="plan-select">Plan Type</InputLabel>
                <Select
                  labelId="plan-select"
                  label="Plan Type"
                  value={plan}
                  onChange={(e) => setPlan(String(e.target.value))}
                >
                  <MenuItem value="">
                    No plan (manual entry)
                  </MenuItem>
                  <MenuItem value="Basic">Basic — £100 / year</MenuItem>
                  <MenuItem value="Pro">Pro — £200 / year</MenuItem>
                  <MenuItem value="Premium">Premium — £300 / year</MenuItem>
                </Select>
              </FormControl>

              <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                <TextField
                  select
                  label="Description"
                  value={descriptionType}
                  onChange={(e) => setDescriptionType(String(e.target.value))}
                  sx={{ flex: 1, minWidth: 220 }}
                  helperText="Select a service type or choose custom."
                  required
                >
                  {DESCRIPTION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Price (£)"
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  value={manualPrice}
                  onChange={(e) => setManualPrice(e.target.value)}
                  sx={{ flex: 1, minWidth: 220 }}
                  helperText="Optional override. Leave blank to calculate from plan details."
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                />
              </Box>

              {descriptionType === "custom" && (
                <TextField
                  label="Custom Description"
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  fullWidth
                  sx={{ mt: 2 }}
                  required
                  placeholder="Describe the service provided"
                />
              )}

              <Box display="flex" gap={2} mt={2}>
                <TextField
                  label="Years of Plan"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={years}
                  onChange={(e) => setYears(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Number of Cameras"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={cameras}
                  onChange={(e) => setCameras(e.target.value)}
                  fullWidth
                />
              </Box>

              <Box mt={3}>
                <Typography variant="subtitle1">
                  Total: {formatCurrency(total)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total reflects either the price above or the calculated plan total.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={sendInvoice}
            disabled={sending || !selectedClient || total <= 0}
          >
            {sending ? "Sending…" : "Send Invoice"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
}


